import SwitchBtn from "components/buttons/SwitchBtn";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { closeModal } from "store/modals/slice";
import Button from "components/buttons/Button";
import {
  getZoneItem,
  handleClearFields,
  handleUpdateFieldDraftItem,
  ZoneItem,
} from "store/zones/slice";
import { Modal } from "components/Modal/Modal";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";

type Props = {};

const ZonesEditModal: React.FC<Props> = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isItemFetched = useAppSelector((store) => store["zones"].isItemFetched);
  const payload = useAppSelector((store) => store["zones"].draftItem);

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const handleData = (
    key: keyof ZoneItem,
    value: string | boolean | number
  ) => {
    dispatch(handleUpdateFieldDraftItem({ [key]: value }));
  };

  const handleApply = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
  };

  const isLoaded = () => {
    if (searchParams.get("entityId") !== null && !isItemFetched) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (searchParams.get("entityId") !== null && !isItemFetched) {
      dispatch(getZoneItem(searchParams.get("entityId") ?? ""));
    }
  }, [searchParams, isItemFetched]);

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>{t("zone")}</Modal.Header>
      <Modal.Content>
        {isLoaded() ? (
          <div className="out-notif-content">
            <div className="out-notif-form-box--item">
              <InnerInput
                value={payload?.zoneName?.toString() ?? ""}
                placeholder={t("zoneName")}
                label={t("zoneName")}
                type="text"
                id="zone-zoneName"
                onChange={(e) => handleData("zoneName", e)}
              />
            </div>
            <div className="out-notif-form-box--item">
              <InnerInput
                value={payload?.accountName?.toString() ?? ""}
                placeholder={t("accountName")}
                label={t("accountName")}
                type="text"
                id="zone-accountName"
                onChange={(e) => handleData("accountName", e)}
              />
            </div>
            <div className="out-notif-form-box--item">
              <InnerInput
                value={payload?.planName?.toString() ?? ""}
                placeholder={t("planName")}
                label={t("planName")}
                type="text"
                id="zone-planName"
                onChange={(e) => handleData("planName", e)}
              />
            </div>
            <div className="out-notif-form-box--item">
              <InnerInput
                value={payload?.type?.toString() ?? ""}
                placeholder={t("type")}
                label={t("type")}
                type="text"
                id="zone-type"
                onChange={(e) => handleData("type", e)}
              />
            </div>
            <div className="out-notif-form-box--item">
              <SwitchBtn
                activeTxt={t("status")}
                isActive={payload?.status === "active"}
                handleClick={() =>
                  handleData(
                    "status",
                    payload?.status === "active" ? "inactive" : "active",
                  )
                }
              />
            </div>
          </div>
        ) : (
          <ModalTabSkeleton />
        )}
      </Modal.Content>
      <Modal.Footer>
        <Button isSecondary txt={t("cancel")} handleClick={handleCloseClick} />
        <Button isPrimary handleClick={handleApply} txt={t("save")} />
      </Modal.Footer>
    </Modal>
  );
};

export default ZonesEditModal;
